import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { useLayoutEffect, useRef, useState } from "react";

const OverflowValueContainer = ({ children, ...props }) => {
  const [chips, input] = children;
  const isArray = Array.isArray(chips);
  const total = isArray ? chips.length : 0;

  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState(total);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    setVisible(total);
  }, [total, width]);

  useLayoutEffect(() => {
    if (!isArray || total === 0) return;
    const el = ref.current;
    if (!el) return;
    const kids = Array.from(el.children);
    if (kids.length === 0) return;
    const lineHeight = Math.max(...kids.map((k) => k.offsetHeight), 0);
    if (lineHeight === 0) return;
    if (el.scrollHeight > lineHeight + 6 && visible > 0) {
      setVisible((v) => v - 1);
    }
  }, [visible, total, width, isArray]);

  const shown = isArray ? Math.min(visible, total) : total;
  const hidden = isArray ? total - shown : 0;

  return (
    <components.ValueContainer {...props}>
      <div
        ref={ref}
        className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5"
      >
        {isArray ? chips.slice(0, shown) : chips}
        {hidden > 0 && (
          <span className="rounded-(--radius-sm) bg-(--surface-2) px-1.5 py-0.5 text-xs font-semibold text-(--text-secondary)">
            +{hidden}
          </span>
        )}
        {input}
      </div>
    </components.ValueContainer>
  );
};

const CLASS_NAMES = {
  control: (state) =>
    `min-h-10 cursor-pointer rounded-(--radius-md) border bg-(--surface-1) px-3 text-sm text-(--text-primary) transition ${
      state.isFocused
        ? "border-(--brand-primary)"
        : "border-(--border-subtle) hover:border-(--brand-primary)/50"
    }`,
  valueContainer: () => "flex min-w-0 flex-1 py-1.5",
  placeholder: () => "text-sm text-(--text-muted)",
  input: () => "m-0 p-0 text-sm text-(--text-primary)",
  group: () => "py-1",
  groupHeading: () =>
    "px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-(--text-muted)",
  menu: () =>
    "mt-2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) shadow-[var(--shadow-popover)]",
  menuList: () => "max-h-[248px] p-1.5",
  option: (state) =>
    `cursor-pointer rounded-(--radius-sm) px-3 py-2 text-sm transition ${
      state.isSelected
        ? "bg-(--pastel-violet) text-(--brand-primary)"
        : state.isFocused
          ? "bg-(--surface-2) text-(--text-primary)"
          : "text-(--text-secondary)"
    }`,
  multiValue: () =>
    "rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2)",
  multiValueLabel: () =>
    "px-2 py-0.5 text-xs font-medium text-(--text-primary)",
  multiValueRemove: () =>
    "flex items-center rounded-r-(--radius-sm) px-1 text-(--text-muted) transition hover:bg-(--surface-muted) hover:text-(--text-primary)",
  indicatorsContainer: () => "gap-1",
  dropdownIndicator: (state) =>
    `px-1 text-(--text-muted) transition ${state.isFocused ? "text-(--text-primary)" : ""}`,
  clearIndicator: () =>
    "px-1 text-(--text-muted) transition hover:text-(--text-primary)",
  indicatorSeparator: () => "hidden",
  noOptionsMessage: () => "px-3 py-2 text-sm text-(--text-muted)",
};

export default function OverflowSelect({
  options,
  value,
  onChange,
  placeholder,
  creatable = false,
  createHint = "Type to add a custom value",
  isMulti = true,
}) {
  const Comp = creatable ? CreatableSelect : Select;
  const flat = options.flatMap((g) => (g.options ? g.options : [g]));
  const toOption = (v) =>
    flat.find((o) => String(o.value) === String(v)) || {
      value: v,
      label: String(v),
    };

  const common = {
    isSearchable: true,
    unstyled: true,
    options,
    placeholder: placeholder ?? "Select…",
    formatCreateLabel: (input) => `Add “${input}”`,
    noOptionsMessage: () => (creatable ? createHint : "No matching options"),
    menuPlacement: "auto",
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 1600 }) },
    classNames: CLASS_NAMES,
  };

  if (!isMulti) {
    return (
      <Comp
        {...common}
        isMulti={false}
        isClearable
        value={value == null || value === "" ? null : toOption(value)}
        onChange={(next) => onChange(next ? next.value : null)}
      />
    );
  }

  return (
    <Comp
      {...common}
      isMulti
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      value={(value || []).map(toOption)}
      components={{ ValueContainer: OverflowValueContainer }}
      onChange={(next) => onChange((next || []).map((o) => o.value))}
    />
  );
}
